import React, { useState, useEffect } from 'react';
import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    onSnapshot
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useNavigate } from 'react-router-dom';
import {
    ClipboardListIcon,
    XCircleIcon,
    EyeIcon
} from '@heroicons/react/outline';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState({
        totalApplications: 0,
        totalTeams: 0,
        totalMentors: 0,
        totalJudges: 0,
        pendingApplications: 0, // not yet evaluated
        evaluatedApplications: 0,
        activeEvents: 0
    });

    const [recentApplications, setRecentApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('AdminDashboard component mounted');
        fetchDashboardData();

        // Set up the real-time listener for recent applications
        const setupRecentApplicationsListener = async () => {
            try {
                console.log('Setting up real-time listener for recent applications...');
                const applicationsQuery = query(
                    collection(db, 'hackathonApplications'),
                    orderBy('submissionDate', 'desc'),
                    limit(5)
                );

                const unsubscribe = onSnapshot(applicationsQuery, (snapshot) => {
                    const applications = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    console.log('Recent applications fetched:', applications);
                    setRecentApplications(applications);
                }, (error) => {
                    console.error('Error in real-time listener for applications:', error);
                });

                return unsubscribe;
            } catch (error) {
                console.error('Error setting up applications listener:', error);
            }
        };

        // Set up the listener and store the unsubscribe function
        let unsubscribeApplications;
        setupRecentApplicationsListener().then(unsubscribe => {
            unsubscribeApplications = unsubscribe;
        });

        // Cleanup function
        return () => {
            if (unsubscribeApplications) {
                console.log('Cleaning up applications listener');
                unsubscribeApplications();
            }
        };
    }, []);

    const fetchDashboardData = async () => {
        try {
            console.log('Fetching dashboard data...');
            setError(null);

            // Fetch total applications
            const applicationsQuery = query(collection(db, 'hackathonApplications'));
            console.log('Fetching applications...');
            const applicationsSnapshot = await getDocs(applicationsQuery);
            const totalApplications = applicationsSnapshot.size;
            console.log('Total applications found:', totalApplications);

            // New logic: only isEvaluated boolean
            let evaluatedApplications = 0;
            applicationsSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.isEvaluated) evaluatedApplications++;
            });
            const pendingApplications = totalApplications - evaluatedApplications;
            console.log('Application counts:', { pendingApplications, evaluatedApplications });

            // Fetch total teams
            const teamsQuery = query(collection(db, 'teams'));
            const teamsSnapshot = await getDocs(teamsQuery);
            const totalTeams = teamsSnapshot.size;
            console.log('Total teams found:', totalTeams);

            // Fetch total mentors
            const mentorsQuery = query(collection(db, 'users'), where('role', '==', 'mentor'));
            const mentorsSnapshot = await getDocs(mentorsQuery);
            const totalMentors = mentorsSnapshot.size;
            console.log('Total mentors found:', totalMentors);

            // Fetch total judges
            const judgesQuery = query(collection(db, 'users'), where('role', '==', 'judge'));
            const judgesSnapshot = await getDocs(judgesQuery);
            const totalJudges = judgesSnapshot.size;
            console.log('Total judges found:', totalJudges);

            // Fetch active events (assuming events collection exists)
            try {
                console.log('Fetching events...');
                const eventsQuery = query(collection(db, 'events'), where('status', '==', 'active'));
                const eventsSnapshot = await getDocs(eventsQuery);
                const activeEvents = eventsSnapshot.size;
                console.log('Total active events found:', activeEvents);

                console.log('Setting dashboard data with events:', {
                    totalApplications,
                    totalTeams,
                    totalMentors,
                    totalJudges,
                    pendingApplications,
                    evaluatedApplications,
                    activeEvents
                });

                setDashboardData({
                    totalApplications,
                    totalTeams,
                    totalMentors,
                    totalJudges,
                    pendingApplications,
                    evaluatedApplications,
                    activeEvents
                });
            } catch (error) {
                console.log('Events collection not found or error, setting activeEvents to 0:', error.message);
                // If events collection doesn't exist, set activeEvents to 0
                setDashboardData(prev => ({
                    ...prev,
                    totalApplications,
                    totalTeams,
                    totalMentors,
                    totalJudges,
                    pendingApplications,
                    evaluatedApplications,
                    activeEvents: 0
                }));
            }

            console.log('Setting loading to false');
            setLoading(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message);
            setLoading(false);
        }
    };

    console.log('AdminDashboard render - loading:', loading, 'error:', error);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Error loading dashboard
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>{error}</p>
                                </div>
                                <div className="mt-4">
                                    <button
                                        onClick={() => {
                                            setError(null);
                                            setLoading(true);
                                            fetchDashboardData();
                                        }}
                                        className="bg-red-100 px-2 py-1 text-sm font-medium text-red-800 rounded-md hover:bg-red-200"
                                    >
                                        Try again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Hello, Admin 👋</h1>
                    <p className="text-gray-600 mt-1">Here's what's happening with your hackathon today.</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Applications */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Applications</p>
                                <p className="text-3xl font-bold text-custom-blue-1 mt-2">{dashboardData.totalApplications}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-50">
                                <ClipboardListIcon className="h-8 w-8 text-custom-blue-1" />
                            </div>
                        </div>
                    </div>
                    {/* Pending Evaluation */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Pending Evaluation</p>
                                <p className="text-3xl font-bold text-orange-500 mt-2">{dashboardData.pendingApplications}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-orange-50">
                                <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {/* Evaluated */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Evaluated</p>
                                <p className="text-3xl font-bold text-purple-600 mt-2">{dashboardData.evaluatedApplications}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-purple-50">
                                <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {/* Total Teams */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hidden lg:block">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Teams</p>
                                <p className="text-3xl font-bold text-indigo-600 mt-2">{dashboardData.totalTeams}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-indigo-50">
                                <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Applications Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Latest Applications</h2>
                            <div className="flex items-center space-x-3">
                                <input
                                    type="text"
                                    placeholder="Search applications..."
                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent"
                                />
                                <button className="bg-custom-green-1 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors">
                                    Export CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Project/Team Info
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Applicant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Submission Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {recentApplications.length > 0 ? (
                                    recentApplications.map((application) => (
                                        <tr key={application.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {application.projectName || application.teamName || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {application.teamSize && `Team Size: ${application.teamSize}`}
                                                        {application.technicalSkills && (
                                                            <>
                                                                {application.teamSize && ' • '}
                                                                {application.technicalSkills.substring(0, 40)}
                                                                {application.technicalSkills.length > 40 ? '...' : ''}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {application.teamLeaderName || application.fullName || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {application.teamLeaderEmail || application.email || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(application)}`}>
                                                    {getStatusText(application)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(application.submissionDate || application.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => navigate(`/eventdashboard/application-details/${application.id}`, {
                                                        state: { application },
                                                    })}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-custom-blue-1 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1"
                                                >
                                                    <EyeIcon className="w-4 h-4 mr-1" />
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                            No applications found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div >
    );

    // Helper functions
    function formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    function getStatusText(application) {
        return application.isEvaluated ? 'Evaluated' : 'Pending Evaluation';
    }

    function getStatusBadgeColor(application) {
        return application.isEvaluated ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800';
    }
};

export default AdminDashboard;
